import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import dayjs from 'dayjs';
import type { BulkDeleteResult } from 'switch-shared/modules/crud/base/base.dto';
import {
  CreateOrLinkUserDto,
  CreateUserDto,
  DeleteUserAbilitiesDto,
  EnhancedAccount,
  EnhancedUser,
  GetUserInvitationsInput,
  GetUserInvitationsOutput,
  ResendInvitationDto,
  SearchNewUserResultDto,
  SearchUserDto,
  SearchUserResultDto,
  SendTestEmailDto,
  UpdateUserDto,
} from 'switch-shared/modules/crud/user/user.dto';
import { User } from 'switch-shared/modules/crud/user/user.entity';

import { baseAPI, setToast } from './baseAPI.service';
import { API_METHODS, ENDPOINT_URLS } from './config/apiTags';

export const userAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [ENDPOINT_URLS.USER],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getUserById: builder.query<User, string>({
        query: (id) => `${ENDPOINT_URLS.USER}/${id}`,
        providesTags: (_res, _err, id) => [{ type: ENDPOINT_URLS.USER, id }],
      }),

      getUserEnhancedById: builder.query<
        EnhancedUser,
        { id: string; applicationGroupId?: string }
      >({
        query: ({ id, applicationGroupId }) => ({
          url: `${ENDPOINT_URLS.USER}/${id}/enhanced`,
          method: API_METHODS.GET,
          params: applicationGroupId ? { applicationGroupId } : undefined,
        }),
        providesTags: (_res, _err, { id }) => [
          { type: ENDPOINT_URLS.USER, id },
        ],
      }),

      getAllUsers: builder.query<SearchUserResultDto, SearchUserDto>({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.USER}`,
            method: API_METHODS.GET,
            params,
          };
        },
        providesTags: (res, _err, _) =>
          res?.data.map((con) => ({
            type: ENDPOINT_URLS.USER,
            id: con._id,
          })) ?? [],
      }),

      searchNewUser: builder.query<SearchNewUserResultDto, SearchUserDto>({
        query: (params) => ({
          url: `${ENDPOINT_URLS.USER}/searchNew`,
          method: API_METHODS.GET,
          params,
        }),
      }),

      getUserAccounts: builder.query<EnhancedAccount[], string>({
        query: (id) => `${ENDPOINT_URLS.USER}/${id}/accounts`,
      }),

      createUser: builder.mutation<User, CreateUserDto>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.USER}`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.USER],
      }),

      createNewUser: builder.mutation<User, CreateOrLinkUserDto>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.USER}/new`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.USER],
      }),

      downloadOnboardingTemplate: builder.mutation<
        void,
        { applicationId?: string; applicationName?: string }
      >({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
          const result = await baseQuery({
            url: `${ENDPOINT_URLS.USER}/onboardingTemplate`,
            params: params.applicationId
              ? { applicationId: params.applicationId }
              : undefined,
            responseHandler: (response) => response.blob(),
          });

          if (result.error) {
            return { error: result.error as FetchBaseQueryError };
          }

          const blob = result.data as Blob;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const timestamp = dayjs().format('YYYY-MM-DD_HH-mm');
          const safeName = (params.applicationName ?? '')
            .trim()
            .replace(/\s+/g, '-');
          const prefix = safeName ? `${safeName}_` : '';
          link.download = `${prefix}User-Onboarding-Template_${timestamp}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          return { data: undefined };
        },
      }),

      updateUser: builder.mutation<User, { id: string; data: UpdateUserDto }>({
        query: ({ id, data }) => ({
          url: `${ENDPOINT_URLS.USER}/${id}`,
          method: API_METHODS.PUT,
          body: data,
        }),
        invalidatesTags: (_res, _err, { id }) => [
          {
            type: ENDPOINT_URLS.USER,
            id,
          },
        ],
      }),

      updateUserPassword: builder.mutation<
        void,
        { data: { userIds: Array<string>; password: string } }
      >({
        query: ({ data }) => ({
          url: `${ENDPOINT_URLS.USER}/updateUserPassword`,
          method: API_METHODS.POST,
          body: data,
        }),
      }),

      verifyUserEmail: builder.mutation<
        void,
        { data: { userIds: Array<string> } }
      >({
        query: ({ data }) => ({
          url: `${ENDPOINT_URLS.USER}/verifyEmail`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: (_res, _err, { data }) =>
          data.userIds.map((userId) => ({
            type: ENDPOINT_URLS.USER,
            id: userId,
          })),
      }),

      bulkDeleteUser: builder.mutation<BulkDeleteResult[], string[]>({
        query: (ids) => ({
          url: `${ENDPOINT_URLS.USER}`,
          method: API_METHODS.DELETE,
          body: { userIds: ids },
        }),
        invalidatesTags: [ENDPOINT_URLS.USER],
      }),

      resendInvitation: builder.mutation<void, ResendInvitationDto>({
        query: (body) => ({
          url: `${ENDPOINT_URLS.USER}/resendInvitation`,
          method: API_METHODS.POST,
          body,
        }),
      }),

      sendTestEmail: builder.mutation<{ sentTo: string }, SendTestEmailDto>({
        query: (body) => ({
          url: `${ENDPOINT_URLS.USER}/sendTestEmail`,
          method: API_METHODS.POST,
          body,
        }),
      }),

      getUserInvitations: builder.query<
        GetUserInvitationsOutput,
        GetUserInvitationsInput
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.USER}/getUserInvitations`,
          method: API_METHODS.GET,
          params,
        }),
      }),
      sendPasswordResetLink: builder.mutation<
        void,
        { userIds: string[]; applicationGroupId: string }
      >({
        query: (body) => ({
          url: `${ENDPOINT_URLS.USER}/sendPasswordResetLink`,
          method: API_METHODS.POST,
          body,
        }),
      }),

      removeUser: builder.mutation<
        void,
        { id: string; data: DeleteUserAbilitiesDto }
      >({
        query: ({ id, data }) => ({
          url: `${ENDPOINT_URLS.USER}/${id}/remove`,
          method: API_METHODS.DELETE,
          body: data,
        }),
        invalidatesTags: (_res, _err, { id }) => [
          {
            type: ENDPOINT_URLS.USER,
            id,
          },
        ],
      }),
    }),
  });

setToast('sendTestEmail', {
  fulfilled: 'Test email sent. Check the inbox (and spam folder).',
});

export const {
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useGetUserEnhancedByIdQuery,
  useGetAllUsersQuery,
  useSearchNewUserQuery,
  useGetUserAccountsQuery,
  useLazyGetAllUsersQuery,
  useCreateUserMutation,
  useCreateNewUserMutation,
  useDownloadOnboardingTemplateMutation,
  useUpdateUserMutation,
  useBulkDeleteUserMutation,
  useUpdateUserPasswordMutation,
  useVerifyUserEmailMutation,
  useResendInvitationMutation,
  useSendTestEmailMutation,
  useGetUserInvitationsQuery,
  useSendPasswordResetLinkMutation,
  useRemoveUserMutation,
} = userAPI;

setToast('bulkDeleteUser', { bulkResults: { noun: 'user', verb: 'delete' } });
